import { useCallback, useEffect, useRef } from 'react';
import type { ApolloQueryResult } from 'apollo-client';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
// eslint-disable-next-line jira/restricted/@atlassian/react-sweet-state
import { createStore, createHook } from '@atlassian/react-sweet-state';
import type { JiraBusinessAvailableFieldsListView } from '../../services/issue-types-and-fields/__generated_apollo__/JiraBusinessAvailableFieldsListView';
import { useIssueTypesAndFieldsService } from '../../services/issue-types-and-fields/index.tsx';

const REFETCH_TIMEOUT = 500;
const MAX_NUMBER_OF_RETRIES = 5;

export type RefetchFieldsReturn = [
	ApolloQueryResult<JiraBusinessAvailableFieldsListView>,
	ApolloQueryResult<JiraBusinessAvailableFieldsListView>,
];

type RefetchFieldsReturnResolveType = (
	value: RefetchFieldsReturn | PromiseLike<RefetchFieldsReturn | null> | null,
) => void;

type PendingRefetchTimeout = {
	timeoutId: ReturnType<typeof setTimeout>;
	resolve: RefetchFieldsReturnResolveType;
};

type RefetchFields = (refetchArgs?: {
	shouldRetry?: (args: RefetchFieldsReturn) => boolean;
}) => Promise<RefetchFieldsReturn | null>;

type State = {
	refetchFields: RefetchFields;
};

const actions = {
	setRefetchFields:
		(refetchFields: RefetchFields) =>
		({ setState }: { setState: (arg1: State) => void }) =>
			setState({ refetchFields }),
} as const;
type Actions = typeof actions;

export const defaultRefetchFields = () => Promise.resolve(null);

const Store = createStore<State, Actions>({
	initialState: { refetchFields: defaultRefetchFields },
	actions,
	name: 'JWMRefetchFieldsStore',
});

const useHook = createHook(Store);

export const useRefetchFields = () => {
	const [{ refetchFields }, { setRefetchFields }] = useHook();

	return { refetchFields, setRefetchFields };
};

// There's a bug in Apollo where it doesn't refetch the query when the component is unMounted,
// because it tries to read the property of an undefined object.
// It usually happens when calling refetch from a Popup and then closing the Popup before request finishes.
// This controller needs to be permanently mounted somewhere in the view within the ApolloProvider.
export const RefetchFieldsController = () => {
	const pendingRefetch = useRef<PendingRefetchTimeout | null>();
	const retryCount = useRef(0);

	const { setRefetchFields } = useRefetchFields();

	const { refetch: refetchViewFields } = useIssueTypesAndFieldsService({
		issueOperation: 'VIEW',
	});
	const { refetch: refetchCreateFields } = useIssueTypesAndFieldsService({
		issueOperation: 'CREATE',
	});

	const setPendingRefetch = (
		refetch: () => Promise<void>,
		resolve: RefetchFieldsReturnResolveType,
	) => {
		const toId = setTimeout(() => {
			pendingRefetch.current = null;
			refetch();
		}, REFETCH_TIMEOUT);
		pendingRefetch.current = {
			timeoutId: toId,
			resolve,
		};
	};

	const clearPendingRefetch = () => {
		if (pendingRefetch.current) {
			const { timeoutId: toId, resolve } = pendingRefetch.current;
			clearTimeout(toId);
			resolve(null);
			pendingRefetch.current = null;
		}
	};

	useEffect(
		() => () => {
			setRefetchFields(defaultRefetchFields);
		},
		[setRefetchFields],
	);

	useEffect(
		() => () => {
			clearPendingRefetch();
		},
		[],
	);

	const refetchFields = useCallback(
		({
			shouldRetry,
		}: {
			shouldRetry?: (args: RefetchFieldsReturn) => boolean;
		} = {}) => {
			clearPendingRefetch();
			return new Promise<RefetchFieldsReturn | null>((resolve, reject) => {
				const refetch = async () => {
					try {
						const response = await Promise.all([refetchViewFields(), refetchCreateFields()]);

						if (!shouldRetry || !shouldRetry(response)) {
							resolve(response);
							retryCount.current = 0;
						} else if (retryCount.current < MAX_NUMBER_OF_RETRIES) {
							retryCount.current += 1;
							// schedule another refetch

							setPendingRefetch(refetch, resolve);
						} else {
							const error = new Error('refetching fields exceeded retries count');

							fireErrorAnalytics({
								meta: {
									id: 'JwmRefetchFields',
									packageName: 'jiraBusinessEntityProject',
									teamName: 'wanjel',
								},
								error,
								attributes: {
									message: `Failed to refetch fields after ${MAX_NUMBER_OF_RETRIES} retries`,
								},
								sendToPrivacyUnsafeSplunk: true,
								skipSentry: true, // this error is handled by the UI, no need to send it to Sentry
							});

							reject(error);
						}
					} catch (error) {
						reject(error);
					}
				};

				// A newly associated field doesn’t show up instantly because of the eventual consistency:
				// recomputation of the fields configuration executes as a background task.
				// That's why we need to wait for a bit here before refetching
				setPendingRefetch(refetch, resolve);
			});
		},
		[refetchCreateFields, refetchViewFields],
	);

	useEffect(() => {
		setRefetchFields(refetchFields);
	}, [refetchFields, setRefetchFields]);

	return null;
};
